import { clearAdminToken, getAdminToken } from "./auth";

const API_BASE = process.env.REACT_APP_API_BASE || "";
export const SIP_CLIENT_DIRECTORY_CHANGED_EVENT = "lead3a:sip-client-directory-changed";
const notifySipClientDirectoryChanged = () => window.dispatchEvent(new Event(SIP_CLIENT_DIRECTORY_CHANGED_EVENT));

async function request(path, options = {}) {
  const token = getAdminToken();
  const headers = {
    Accept: "application/json",
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearAdminToken();
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const data = await response.json();
      message = data?.message || data?.error || message;
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        message = `${message}: ${data.errors.join("; ")}`;
      }
    } catch {}
    const method = String(options.method || "GET").toUpperCase();
    const endpoint = `${API_BASE}${path}`;
    const error = new Error(`${message} [${method} ${endpoint}, HTTP ${response.status}]`);
    error.status = response.status;
    error.method = method;
    error.endpoint = endpoint;
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export function loginAdmin(payload) {
  return request("/api/admin/login", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function fetchAdminMe() {
  return request("/api/admin/me");
}

export function fetchAdminComplexes(options = {}) {
  return request("/api/admin/complexes", options);
}

export function fetchAdminComplexById(id) {
  return request(`/api/admin/complexes/${id}`);
}

export function searchAdminComplexes(payload, options = {}) {
  return request("/api/admin/complexes/search", {
    ...options,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function bulkPatchAdminComplexes(payload) {
  return request("/api/admin/complexes/bulk", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function bulkUpdateSipStatus(payload, sourceSection = "admin") {
  return request("/api/admin/complexes/sip-clients/bulk-status", {
    method: "PATCH",
    body: JSON.stringify(payload),
    headers: { "X-Admin-Source-Section": sourceSection },
  });
}

export function bulkApplySipClients(payload, sourceSection = "clients") {
  return request("/api/admin/complexes/sip-clients/bulk-apply", {
    method: "PATCH",
    body: JSON.stringify(payload),
    headers: { "X-Admin-Source-Section": sourceSection },
  });
}

export function resolveSipClients(payload, options = {}) {
  return request("/api/admin/complexes/sip-clients/resolve", {
    ...options,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function bulkEditSipClients(payload, sourceSection = "admin") {
  return request("/api/admin/complexes/sip-clients/bulk-edit", {
    method: "PATCH",
    body: JSON.stringify(payload),
    headers: { "X-Admin-Source-Section": sourceSection },
  });
}

export function resolveAdminComplexes(ids) {
  return request("/api/admin/complexes/bulk-resolve", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export function bulkEditAdminComplexes(payload, sourceSection = "admin") {
  return request("/api/admin/complexes/bulk-edit", {
    method: "PATCH",
    body: JSON.stringify(payload),
    headers: { "X-Admin-Source-Section": sourceSection },
  });
}

export function createAdminComplex(payload) {
  return request("/api/admin/complexes", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function patchAdminComplex(id, payload) {
  return request(`/api/admin/complexes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function fetchSipClientOptions() {
  return request("/api/admin/sip-clients/options");
}

export function fetchSipClientDirectory() { return request("/api/admin/sip-clients"); }

export function searchSipClientPrices(payload, options = {}) {
  return request("/api/admin/sip-client-prices/search", { ...options, method: "POST", body: JSON.stringify(payload) });
}

export function bulkSaveSipClientPrices(items) {
  return request("/api/admin/sip-client-prices/bulk", {
    method: "PATCH",
    headers: { "X-Admin-Source-Section": "prices" },
    body: JSON.stringify({ items, sourceSection: "prices" }),
  });
}

export function fetchAdminCities() { return request("/api/admin/cities"); }
export function createAdminCity(payload) { return request("/api/admin/cities", { method: "POST", body: JSON.stringify(payload) }); }
export function patchAdminCity(id, payload) { return request(`/api/admin/cities/${id}`, { method: "PATCH", body: JSON.stringify(payload) }); }
export function deleteAdminCity(id) { return request(`/api/admin/cities/${id}`, { method: "DELETE" }); }

export function fetchSipClientAssignments(clientId, filters = {}, options = {}) {
  const params = new URLSearchParams({ clientId });
  if (filters.clientStatus) params.set("clientStatus", filters.clientStatus);
  if (filters.launchStatus) params.set("launchStatus", filters.launchStatus);
  if (filters.complexStatus) params.set("complexStatus", filters.complexStatus);
  if (filters.query) params.set("query", filters.query);
  return request(`/api/admin/complexes/sip-client-assignments?${params.toString()}`, options);
}

export async function createSipClientOption(payload) {
  const result = await request("/api/admin/sip-clients", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  notifySipClientDirectoryChanged();
  return result;
}

export async function patchSipClientOption(id, payload) {
  const result = await request(`/api/admin/sip-clients/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  notifySipClientDirectoryChanged();
  return result;
}

export async function deleteSipClientOption(id) {
  const result = await request(`/api/admin/sip-clients/${id}`, {
    method: "DELETE",
  });
  notifySipClientDirectoryChanged();
  return result;
}

export function fetchSipEndpoints(options = {}) {
  return request("/api/admin/sip-endpoints", options);
}

export function createSipEndpoint(payload) {
  return request("/api/admin/sip-endpoints", { method: "POST", body: JSON.stringify(payload) });
}

export function patchSipEndpoint(id, payload) {
  return request(`/api/admin/sip-endpoints/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) });
}

export function deleteSipEndpoint(id) {
  return request(`/api/admin/sip-endpoints/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function fetchClientActivityLogs(options = {}) {
  const query = options.period ? `?period=${encodeURIComponent(options.period)}` : "";
  return request(`/api/admin/client-activity-logs${query}`);
}

export function deleteOldClientActivityLogs() {
  return request("/api/admin/client-activity-logs/old", {
    method: "DELETE",
  });
}

export function fetchAdminOperations(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== "" && value !== null && typeof value !== "undefined") params.set(key, value);
  });
  const query = params.toString();
  return request(`/api/admin/operations${query ? `?${query}` : ""}`);
}

export function fetchAdminOperation(id) {
  return request(`/api/admin/operations/${id}`);
}

const telegramPath = (path = "") => `/api/admin/telegram-status${path}`;
export const fetchTelegramSources = () => request(telegramPath("/sources"));
export const fetchTelegramSource = (id) => request(telegramPath(`/sources/${encodeURIComponent(id)}`));
export const patchTelegramSource = (id, payload) => request(telegramPath(`/sources/${encodeURIComponent(id)}`), { method: "PATCH", body: JSON.stringify(payload) });
export const fetchTelegramReadiness = (id) => request(telegramPath(`/sources/${encodeURIComponent(id)}/readiness`));
export const createTelegramTrigger = (sourceId, action, payload) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/triggers/${action}`), { method: "POST", body: JSON.stringify(payload) });
export const patchTelegramTrigger = (sourceId, triggerId, payload) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/triggers/${encodeURIComponent(triggerId)}`), { method: "PATCH", body: JSON.stringify(payload) });
export const deleteTelegramTrigger = (sourceId, triggerId) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/triggers/${encodeURIComponent(triggerId)}`), { method: "DELETE" });
export const createTelegramAlias = (sourceId, payload) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/aliases`), { method: "POST", body: JSON.stringify(payload) });
export const patchTelegramAlias = (sourceId, aliasId, payload) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/aliases/${encodeURIComponent(aliasId)}`), { method: "PATCH", body: JSON.stringify(payload) });
export const patchTelegramAliasesStartAllowed = (sourceId, startAllowed) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/aliases-start-allowed`), { method: "PATCH", body: JSON.stringify({ startAllowed }) });
export const deleteTelegramAlias = (sourceId, aliasId) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/aliases/${encodeURIComponent(aliasId)}`), { method: "DELETE" });
export const addTelegramAliasBinding = (sourceId, aliasId, payload) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/aliases/${encodeURIComponent(aliasId)}/bindings`), { method: "POST", body: JSON.stringify(payload) });
export const deleteTelegramAliasBinding = (sourceId, aliasId, bindingId) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/aliases/${encodeURIComponent(aliasId)}/bindings/${encodeURIComponent(bindingId)}`), { method: "DELETE" });
export const patchTelegramSenderPolicy = (sourceId, senderPolicy) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/sender-policy`), { method: "PATCH", body: JSON.stringify({ senderPolicy }) });
export const addTelegramSender = (sourceId, payload) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/senders`), { method: "POST", body: JSON.stringify(payload) });
export const deleteTelegramSender = (sourceId, senderId) => request(telegramPath(`/sources/${encodeURIComponent(sourceId)}/senders/${encodeURIComponent(senderId)}`), { method: "DELETE" });
export const fetchTelegramEvents = (filters = {}) => { const params = new URLSearchParams(); Object.entries(filters).forEach(([key, value]) => { if (value !== "" && value !== false && value != null) params.set(key, value); }); const query = params.toString(); return request(telegramPath(`/events${query ? `?${query}` : ""}`)); };
export const fetchTelegramEvent = (id) => request(telegramPath(`/events/${encodeURIComponent(id)}`));
export const fetchTelegramHealth = () => request(telegramPath("/health"));
export const fetchTelegramAutomation = () => request(telegramPath("/settings/automation"));
export const patchTelegramAutomation = (enabled) => request(telegramPath("/settings/automation"), { method: "PATCH", body: JSON.stringify({ enabled }) });

const marketcallPath = (path = "") => `/api/admin/marketcall${path}`;
export const fetchMarketcallStatus = (options = {}) => request(marketcallPath("/status"), options);
export const fetchMarketcallPrograms = (filters = {}, options = {}) => { const params = new URLSearchParams(); Object.entries(filters).forEach(([key, value]) => { if (value !== "" && value !== false && value != null) params.set(key, value); }); return request(marketcallPath(`/programs?${params}`), options); };
export const fetchMarketcallProgram = (id) => request(marketcallPath(`/programs/${encodeURIComponent(id)}`));
export const deleteMarketcallProgram = (programId) => request(marketcallPath(`/programs/${encodeURIComponent(programId)}`), { method: "DELETE" });
export const fetchAvailableMarketcallPrograms = (filters = {}) => { const params = new URLSearchParams(); Object.entries(filters).forEach(([key, value]) => { if (value !== "" && value != null) params.set(key, value); }); return request(marketcallPath(`/available-programs?${params}`)); };
export const connectMarketcallProgram = (id) => request(marketcallPath(`/programs/${encodeURIComponent(id)}/connect`), { method: "POST", body: "{}" });
export const searchMarketcallComplexes = (q) => request(marketcallPath(`/complexes?q=${encodeURIComponent(q)}`));
export const reconcileMarketcall = () => request(marketcallPath("/reconcile"), { method: "POST", body: "{}" });
export const addMarketcallBinding = (id, payload) => request(marketcallPath(`/programs/${encodeURIComponent(id)}/bindings`), { method: "POST", body: JSON.stringify(payload) });
export const deleteMarketcallBinding = (id, key) => request(marketcallPath(`/programs/${encodeURIComponent(id)}/bindings/${encodeURIComponent(key)}`), { method: "DELETE" });
export const pauseMarketcall = (id) => request(marketcallPath(`/programs/${encodeURIComponent(id)}/pause`), { method: "POST", body: "{}" });
export const resumeMarketcall = (id) => request(marketcallPath(`/programs/${encodeURIComponent(id)}/resume`), { method: "POST", body: "{}" });

export function saveSipClient(id, sipClientId, payload) {
  return request(`/api/admin/complexes/${id}/sip-clients${sipClientId ? `/${encodeURIComponent(sipClientId)}` : ""}`, {
    method: sipClientId ? "PATCH" : "POST",
    body: JSON.stringify(payload),
  });
}

export function deleteSipClient(id, sipClientId) {
  return request(`/api/admin/complexes/${id}/sip-clients/${encodeURIComponent(sipClientId)}`, {
    method: "DELETE",
  });
}

export function setSipClientStatus(id, sipClientId, status) {
  return request(`/api/admin/complexes/${id}/sip-clients/${encodeURIComponent(sipClientId)}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export function applySipClient(id, sipClientId) {
  return request(`/api/admin/complexes/${id}/sip-clients/${encodeURIComponent(sipClientId)}/apply`, {
    method: "POST",
  });
}

export function stopCurrentSipClient(id) {
  return request(`/api/admin/complexes/${id}/sip-clients/stop`, {
    method: "POST",
  });
}

export function deleteAdminComplex(id) {
  return request(`/api/admin/complexes/${id}`, {
    method: "DELETE",
  });
}
